// Whiteboard engine — the two off-screen renderers of the same primitives
// the screen draws (scene.js):
//   drawCanvas(ctx, …)  a Canvas 2D context → the PNG export
//   drawPdf(doc, …)     a jsPDF document   → the PDF export, in VECTORS
//                       (lines, curves and words stay sharp at any zoom; only
//                       pictures are pictures, at their full resolution)

import { primitives, dashArray } from './scene.js';
import { rotate } from './geometry.js';
import { fontCss, PDF_FONTS } from './text.js';

// '#abc' / '#aabbcc' / '#aabbccdd' → { hex: '#aabbcc', alpha }
export function parseColor(c) {
  if (!c || c === 'transparent') return null;
  let h = String(c).replace('#', '');
  if (h.length === 3 || h.length === 4) h = h.split('').map((x) => x + x).join('');
  const alpha = h.length === 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1;
  return { hex: `#${h.slice(0, 6)}`, alpha };
}

function trace(ctx, ops) {
  for (const op of ops) {
    switch (op[0]) {
      case 'M': ctx.moveTo(op[1], op[2]); break;
      case 'L': ctx.lineTo(op[1], op[2]); break;
      case 'Q': ctx.quadraticCurveTo(op[1], op[2], op[3], op[4]); break;
      case 'C': ctx.bezierCurveTo(op[1], op[2], op[3], op[4], op[5], op[6]); break;
      case 'Z': ctx.closePath(); break;
      default: break;
    }
  }
}

// images: Map fileId → a drawable (HTMLImageElement / ImageBitmap / canvas).
// pixelScale: device pixels per board unit (shadows are in device pixels).
export function drawCanvas(ctx, elements, images, pixelScale = 1) {
  for (const el of elements) {
    const { prims, angle, cx, cy, opacity } = primitives(el);
    ctx.save();
    ctx.globalAlpha = opacity;
    if (angle) { ctx.translate(cx, cy); ctx.rotate(angle); ctx.translate(-cx, -cy); }
    for (const p of prims) {
      if (p.k === 'path') {
        ctx.beginPath();
        trace(ctx, p.ops);
        if (p.fill) {
          ctx.save();
          if (p.shadow) { ctx.shadowColor = 'rgba(15, 23, 42, 0.18)'; ctx.shadowBlur = 14 * pixelScale; ctx.shadowOffsetY = 4 * pixelScale; }
          ctx.fillStyle = p.fill;
          ctx.fill('nonzero');
          ctx.restore();
        }
        if (p.stroke) {
          ctx.strokeStyle = p.stroke;
          ctx.lineWidth = p.width;
          ctx.lineCap = p.cap || 'round';
          ctx.lineJoin = p.join || 'round';
          ctx.setLineDash(dashArray(p.dash, p.width) || []);
          ctx.stroke();
        }
      } else if (p.k === 'text') {
        ctx.font = fontCss({ size: p.size, font: p.font, bold: p.bold, italic: p.italic });
        ctx.fillStyle = p.color;
        ctx.textBaseline = 'alphabetic';
        ctx.textAlign = 'left';
        if (p.rot) { ctx.save(); ctx.translate(p.ox, p.oy); ctx.rotate(p.rot); ctx.translate(-p.ox, -p.oy); }
        for (const l of p.lines) ctx.fillText(l.text, l.x, l.y);
        if (p.rot) ctx.restore();
      } else if (p.k === 'image') {
        const img = images?.get(p.fileId);
        if (img) ctx.drawImage(img, p.x, p.y, p.w, p.h);
        else { ctx.fillStyle = '#e2e8f0'; ctx.fillRect(p.x, p.y, p.w, p.h); }
      }
    }
    ctx.restore();
  }
}

// ---- PDF ---------------------------------------------------------------------------
// map: { s, ox, oy, mx, my } — page point = (board − o) × s + m
// images: Map fileId → { data, format: 'PNG' | 'JPEG', width, height, rotated? (canvas) }
export function drawPdf(doc, elements, images, map) {
  const P = (x, y) => [(x - map.ox) * map.s + map.mx, (y - map.oy) * map.s + map.my];
  for (const el of elements) {
    const { prims, angle, cx, cy, opacity } = primitives(el);
    const T = angle ? (x, y) => { const [rx, ry] = rotate(x, y, cx, cy, angle); return P(rx, ry); } : P;
    for (const p of prims) {
      if (p.k === 'path') pdfPath(doc, p, T, opacity, map.s);
      else if (p.k === 'text') pdfText(doc, p, T, opacity, angle, map.s);
      else if (p.k === 'image') pdfImage(doc, p, el, images, P, opacity, map.s);
    }
  }
}

function withOpacity(doc, fillA, strokeA, fn) {
  const needs = fillA < 0.999 || strokeA < 0.999;
  if (needs) {
    doc.saveGraphicsState();
    doc.setGState(new doc.GState({ opacity: fillA, 'stroke-opacity': strokeA }));
  }
  try { fn(); } finally { if (needs) doc.restoreGraphicsState(); }
}

function emitPath(doc, ops, T) {
  let cur = [0, 0];
  for (const op of ops) {
    if (op[0] === 'M') { const q = T(op[1], op[2]); doc.moveTo(q[0], q[1]); cur = q; } else if (op[0] === 'L') {
      const q = T(op[1], op[2]); doc.lineTo(q[0], q[1]); cur = q;
    } else if (op[0] === 'Q') {
      // a quadratic is an exact cubic: control points at ⅔ towards the quadratic's one
      const c = T(op[1], op[2]); const e = T(op[3], op[4]);
      const c1 = [cur[0] + (2 / 3) * (c[0] - cur[0]), cur[1] + (2 / 3) * (c[1] - cur[1])];
      const c2 = [e[0] + (2 / 3) * (c[0] - e[0]), e[1] + (2 / 3) * (c[1] - e[1])];
      doc.curveTo(c1[0], c1[1], c2[0], c2[1], e[0], e[1]); cur = e;
    } else if (op[0] === 'C') {
      const c1 = T(op[1], op[2]); const c2 = T(op[3], op[4]); const e = T(op[5], op[6]);
      doc.curveTo(c1[0], c1[1], c2[0], c2[1], e[0], e[1]); cur = e;
    } else if (op[0] === 'Z') doc.close();
  }
}

function pdfPath(doc, p, T, opacity, s) {
  const fill = parseColor(p.fill); const stroke = parseColor(p.stroke);
  if (!fill && !stroke) return;
  if (fill && p.shadow) {
    // a soft hint of the note's shadow (PDF has no blur)
    withOpacity(doc, 0.08 * opacity, 1, () => {
      doc.setFillColor('#0f172a');
      emitPath(doc, p.ops, (x, y) => { const q = T(x, y); return [q[0] + 1.5 * s, q[1] + 3 * s]; });
      doc.fill();
    });
  }
  if (fill) {
    withOpacity(doc, fill.alpha * opacity, 1, () => {
      doc.setFillColor(fill.hex);
      emitPath(doc, p.ops, T);
      doc.fill();
    });
  }
  if (stroke) {
    withOpacity(doc, 1, stroke.alpha * opacity, () => {
      doc.setDrawColor(stroke.hex);
      doc.setLineWidth(Math.max(0.1, p.width * s));
      doc.setLineCap(p.cap === 'butt' ? 'butt' : 'round');
      doc.setLineJoin(p.join === 'miter' ? 'miter' : 'round');
      const dash = dashArray(p.dash, p.width);
      doc.setLineDashPattern(dash ? dash.map((d) => d * s) : [], 0);
      emitPath(doc, p.ops, T);
      doc.stroke();
      if (dash) doc.setLineDashPattern([], 0);
    });
  }
}

function pdfText(doc, p, T, opacity, angle, s) {
  const c = parseColor(p.color) || { hex: '#1e293b', alpha: 1 };
  const style = p.bold && p.italic ? 'bolditalic' : p.bold ? 'bold' : p.italic ? 'italic' : 'normal';
  withOpacity(doc, c.alpha * opacity, c.alpha * opacity, () => {
    doc.setFont(PDF_FONTS[p.font] || 'helvetica', style);
    doc.setFontSize(p.size * s);
    doc.setTextColor(c.hex);
    const deg = -((angle + (p.rot || 0)) * 180) / Math.PI;
    for (const l of p.lines) {
      if (!l.text) continue;
      const [rx, ry] = p.rot ? rotate(l.x, l.y, p.ox, p.oy, p.rot) : [l.x, l.y];
      const [x, y] = T(rx, ry);
      doc.text(l.text, x, y, { baseline: 'alphabetic', angle: deg || undefined });
    }
  });
}

// A turned picture comes pre-turned (exporter: key `el:<element id>`, an
// axis-aligned box on the board); an upright one is placed as it is.
function pdfImage(doc, p, el, images, P, opacity, s) {
  const turned = el.angle ? images?.get(`el:${el.id}`) : null;
  const img = images?.get(p.fileId);
  if (!turned && !img) return;
  withOpacity(doc, opacity, opacity, () => {
    if (turned) {
      const [x, y] = P(turned.x, turned.y);
      doc.addImage(turned.data, 'PNG', x, y, turned.w * s, turned.h * s, undefined, 'FAST');
      return;
    }
    const [x, y] = P(p.x, p.y);
    doc.addImage(img.data, img.format, x, y, p.w * s, p.h * s, `wb-${p.fileId}`, 'FAST');
  });
}
